import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size";
import { CC_RECIPIENT, MAX_RECORDS, TO_RECIPIENT } from "../../constants/notification-key";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import EnrolmentColumns from "../../models/ColumnDefinitions/EnrolmentColumns";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import CategorySearch from "../../models/search/CategorySearch";
import CompanySearch from "../../models/search/CompanySearch";
import DepartmentSearch from "../../models/search/DepartmentSearch";
import EnrolmentSearch from "../../models/search/EnrolmentSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { SettingData } from "../../models/SettingData";
import { TabSummary } from "../../models/TabSummary";
import { hideColumns } from "../../models/utilities/columnHelper";
import { getCategoryNameWithPagination } from "../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../services/departmentService";
import { getEnrolments } from "../../services/enrolmentService";
import { handleManage } from "../../services/notificationService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { appendFilter } from "../../utilities/filters";
import ActivityFrame from "../activity-frame/activity-frame";
import AssignWizard from "../assignWizard/AssignWizard";
import { useDialog } from "../dialog/DialogContext";
import TableTab from "../tabs/table-tab";
import Tabs from "../tabs/tabs";

interface RecipientsProps {
    ccRecipients: string[];
    toRecipients: string[];
    reloadPage: () => void;
}

const Recipients: FC<RecipientsProps> = ({ ccRecipients, toRecipients, reloadPage }) => {

    const [selectedTab, setSelectedTab] = useState(0);
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.sendTo), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.copyTo), badge: 0, showInitial: true, isActive: true },
    ];
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedToRecipients, setSelectedToRecipients] = useState<string[]>(toRecipients);
    const [selectedCCRecipients, setSelectedCCRecipients] = useState<string[]>(ccRecipients);
    const [busy, setBusy] = useState(false);
    const { values } = useFormikContext<any>();
    const dialog = useDialog();
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    const [lookup] = useState<Lookup[]>([{
        name: LookupTypes.site,
        values: [],
        dataMethod: getSiteNamesWithPagination,
        searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.company,
        values: [],
        dataMethod: getCompanyNamesWithPagination,
        searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.department,
        values: [],
        dataMethod: getDepartmentNamesWithPagination,
        searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.profile_id,
        values: [],
        dataMethod: getCategoryNameWithPagination,
        searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
    }]);
    const columns = hideColumns(EnrolmentColumns, [
        "totalTags",
        "category",
        "uid",
        "gender",
        "title",
        "totalAccessGroups",
        "suffix",
        "enabled",
        "masterTypeName",
        "site",
    ])

    useEffect(() => {
        setSelectedCCRecipients(ccRecipients);
        setSelectedToRecipients(toRecipients);

        dataRefresh(getDataMethod(selectedTab));

        const updatedTabs = updateTabBadgeTotal(
            0, toRecipients.length,
            updateTabBadgeTotal(1, ccRecipients.length, tabs)
        );
        setTabs(updatedTabs);
    }, [toRecipients, ccRecipients, showManagedModal]);

    const getReportTitle = (selectedTab: number): string => {
        return selectedTab === 0 ? translate.byKey(TranslationKeys.sendToRecipient) : translate.byKey(TranslationKeys.copyToRecipient);
    }

    const getDataMethod = (selected: number): (listParameters?: ListQuery) => Promise<ListData> => {
        const recipients: string = selected === 0 ? toRecipients.toString() : ccRecipients.toString();
        if (recipients) {
            return (listParameters?: ListQuery) => getEnrolments(translate, {
                ...listParameters,
                filters: appendFilter(
                    recipients,
                    "Recipients",
                    "id",
                    listParameters?.filters ?? [],
                    "=in=")
            })
        }

        return (listParameters?: ListQuery) => {
            return new Promise((resolve, reject) => {
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } })
            })
        }
    }

    const onTabSelect = (selected: number) => {
        dataRefresh(getDataMethod(selected));
        setSelectedTab(selected);
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    };

    const onSubmit = (): void => {
        if (selectedToRecipients.length < 1) {
            toast.error(translate.byKey(TranslationKeys.atLeastOneSendToRecipientNeedsToBeSelected));
        }
        else {
            const settingDataRefForToRecipients = values.settingDatasRef.filter(settingData => settingData.elementSysname === "TO_RECIPIENT")
            const settingDataRefForCCRecipients = values.settingDatasRef.filter(settingData => settingData.elementSysname === "CC_RECIPIENT")

            const removeToRecipents = settingDataRefForToRecipients
                .filter((settingDataRef) => !selectedToRecipients.includes(settingDataRef.sdValstring))
                .map((filteredSettingDataRef) => filteredSettingDataRef.id);

            const removeCCRecipents = settingDataRefForCCRecipients
                .filter((settingDataRef) => !selectedCCRecipients.includes(settingDataRef.sdValstring))
                .map((filteredSettingDataRef) => filteredSettingDataRef.id);

            const newToRecipients = selectedToRecipients
                .filter(id => !settingDataRefForToRecipients.some(obj => obj.sdValstring === id))
                .filter(id => !selectedCCRecipients.includes(id));

            const newCCRecipients = selectedCCRecipients
                .filter(id => !settingDataRefForCCRecipients.some(obj => obj.sdValstring === id))
                .filter(id => !selectedToRecipients.includes(id));

            const settingDataToAdd: SettingData[] = selectedTab === 0
                ? newToRecipients.map(sdValstring => ({
                    sdValstring,
                    eSysnameLookup: TO_RECIPIENT,
                    relationship: [{
                        id: values.vruleActionId,
                        type: "ruleAction",
                        relationType:"vruleAction"
                    },
                    {
                        id: sdValstring,
                        type: "master"
                    }
                    ]
                }))
                : newCCRecipients.map(sdValstring => ({
                    sdValstring,
                    eSysnameLookup: CC_RECIPIENT,
                    relationship:[
                        {
                            id: values.vruleActionId,
                            type: "ruleAction",
                            relationType:"vruleAction"
                        },
                        {
                            id: sdValstring,
                            type: "master"
                        }
                    ]
                }));

            setBusy(true)
            handleManage(settingDataToAdd, selectedTab === 0 ? removeToRecipents :removeCCRecipents)
            .then(async() => {
                toast.success(translate.byKey(TranslationKeys.recordUpdated));
                await reloadPage();
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
            .finally(() => {
                setBusy(false)
            })
        }
    }

    const validateSelectedIds = (ids: string[]) => {
        if (selectedTab === 0) {
            const newToRecipients = ids.filter(id => !selectedCCRecipients.includes(id));

            if (newToRecipients.length >= MAX_RECORDS) {
                toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
                setSelectedToRecipients([...selectedToRecipients])
            } else {
                setSelectedToRecipients([...newToRecipients])
            }
        }
        else {
            const newCCRecipients = ids.filter(id => !selectedToRecipients.includes(id));

            if (newCCRecipients.length >= 21) {
                toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
                setSelectedCCRecipients([...selectedCCRecipients])
            } else {
                setSelectedCCRecipients([...newCCRecipients])
            }
        }
    }

    return <>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <Tabs
                tabs={tabs}
                onSelect={onTabSelect}
                selected={selectedTab}
                add={false}
                onAdd={() => { }}
                isFlat={true}
                className="w-full">
                <div className="w-full flex flex-col">
                    <div className="mb-3 w-full border-b border-gray-400" />
                    <TableContextProvider
                        dataMethod={getDataMethod(selectedTab)}
                        manualDataRefresh={manualDataRefresh}
                        tableDescriptor={columns}
                        searchDescriptor={EnrolmentSearch}
                        useTable
                        usePagination
                        useFilter
                        useSearch
                        useSort
                        maxPageSize={DEF_PG_SIZE_SUB}
                        lookup={lookup}
                        canExportReport={false}>
                        <TableTab
                            title={getReportTitle(selectedTab)}
                            selectRows={false}
                            cyName="recipients">
                            <button type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                                onClick={() => setShowManagedModal(true)}>
                                {translate.byKey(TranslationKeys.manage)}
                            </button>
                        </TableTab>
                    </TableContextProvider>
                </div>
            </Tabs>
            <AssignWizard
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                title={getReportTitle(selectedTab)}
                lookup={lookup}
                dataMethod={(listParameters) => getEnrolments(translate, {
                    ...listParameters,
                    filters: appendFilter(
                        false,
                        "Email Address",
                        "mstEmailAddress",
                        listParameters?.filters ?? [],
                        "=isnull=",
                    ),
                })}
                columnDescriptors={{
                    columns: columns,
                    searchColumns: EnrolmentSearch,
                }}
                handleOnSubmit={onSubmit}
                selectedIds={selectedTab === 0 ? selectedToRecipients : selectedCCRecipients}
                setSelectedIds={validateSelectedIds}
                alwaysSelectedAndDisabled={selectedTab === 0 ? selectedCCRecipients : selectedToRecipients}
            />
        </ActivityFrame>
        </>
}

export default Recipients;
